import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IoClose, IoCheckmark, IoOpenOutline } from 'react-icons/io5'
import { useOdds } from '../context/OddsContext'
import { getBookmakerLinks, type BookmakerLink } from '../api'

interface OddsComparisonModalProps {
  isOpen: boolean
  onClose: () => void
  fixtureId: number
  marketSlug: string
  outcome: string
  line?: number | null
  selectionLabel: string
  ourProbability?: number // Our calculated probability
}

function useFocusTrap<T extends HTMLElement>(isActive: boolean) {
  const ref = useRef<T>(null)

  useEffect(() => {
    if (!isActive || !ref.current) return

    const el = ref.current
    const focusableEls = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const first = focusableEls[0]
    const last = focusableEls[focusableEls.length - 1]

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last?.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first?.focus()
      }
    }

    first?.focus()
    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [isActive])

  return ref
}

export function OddsComparisonModal({
  isOpen,
  onClose,
  fixtureId,
  marketSlug,
  outcome,
  line,
  selectionLabel,
  ourProbability,
}: OddsComparisonModalProps) {
  const {
    getFixtureOddsData,
    bookmakers,
    formatOdds,
    preferredBookmaker,
  } = useOdds()

  const modalRef = useFocusTrap<HTMLDivElement>(isOpen)
  const [bookmakerLinks, setBookmakerLinks] = useState<Map<string, string>>(new Map())

  // Fetch bookmaker links when modal opens
  useEffect(() => {
    if (isOpen && bookmakerLinks.size === 0) {
      getBookmakerLinks({
        fixture_id: fixtureId,
        market_type: marketSlug,
        selection: selectionLabel,
      })
        .then(({ links }) => {
          const linkMap = new Map<string, string>()
          links.forEach((link) => {
            linkMap.set(link.bookmaker_slug, link.url)
          })
          setBookmakerLinks(linkMap)
        })
        .catch((err) => console.error('Failed to fetch bookmaker links:', err))
    }
  }, [isOpen, fixtureId, marketSlug, selectionLabel, bookmakerLinks.size])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // Get all bookmaker odds for this selection
  const oddsData = useMemo(() => {
    if (!isOpen) return null

    const fixtureOdds = getFixtureOddsData(fixtureId)
    if (!fixtureOdds) return null

    const market = fixtureOdds.markets.find(m => m.market_slug === marketSlug)
    if (!market) return null

    // Find matching outcome (with line if applicable)
    const outcomeData = market.outcomes.find(o => {
      const outcomeMatches = o.outcome === outcome
      if (line != null) {
        return outcomeMatches && o.line === line
      }
      return outcomeMatches
    })

    if (!outcomeData) return null

    // Build sorted list of all bookmaker odds
    const allOdds = Object.entries(outcomeData.by_bookmaker || {})
      .map(([slug, odds]) => {
        const bookmaker = bookmakers.find(b => b.slug === slug)
        const impliedProb = odds > 0 ? (1 / odds) * 100 : 0
        const edge = ourProbability != null ? ourProbability - impliedProb : null
        return {
          slug,
          name: bookmaker?.name || slug,
          odds: odds as number,
          impliedProb,
          edge,
          isPreferred: slug === preferredBookmaker?.slug,
        }
      })
      .filter(item => item.odds > 0)
      .sort((a, b) => b.odds - a.odds) // Best odds first

    return {
      marketName: market.market_name,
      bestOdds: allOdds[0],
      allOdds,
    }
  }, [isOpen, fixtureId, marketSlug, outcome, line, getFixtureOddsData, bookmakers, ourProbability, preferredBookmaker])

  if (!isOpen) return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="odds-comparison-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="odds-comparison-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="odds-comparison-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="odds-comparison-header">
          <div className="odds-comparison-title-section">
            <h2 id="odds-comparison-title">Compare Odds</h2>
            <span className="odds-comparison-selection">{selectionLabel}</span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <IoClose size={20} />
          </button>
        </div>

        <div className="odds-comparison-body">
          {oddsData && oddsData.allOdds.length > 0 ? (
            <>
              {/* Our probability banner */}
              {ourProbability != null && (
                <div className="odds-comparison-probability">
                  <span className="prob-label">Our probability:</span>
                  <span className="prob-value">{ourProbability}%</span>
                </div>
              )}

              {/* Odds table */}
              <div className="odds-comparison-table">
                <div className="odds-table-header">
                  <span className="col-bookmaker">Bookmaker</span>
                  <span className="col-odds">Odds</span>
                  <span className="col-implied">Implied</span>
                  {ourProbability != null && <span className="col-edge">Edge</span>}
                  <span className="col-bet">Bet</span>
                </div>
                <div className="odds-table-body">
                  {oddsData.allOdds.map((item, idx) => {
                    const betLink = bookmakerLinks.get(item.slug)
                    return (
                      <div
                        key={item.slug}
                        className={`odds-table-row ${idx === 0 ? 'best' : ''} ${item.isPreferred ? 'preferred' : ''}`}
                      >
                        <span className="col-bookmaker">
                          {item.name}
                          {idx === 0 && <span className="best-badge">Best</span>}
                          {item.isPreferred && <IoCheckmark className="preferred-icon" size={14} />}
                        </span>
                        <span className="col-odds">{formatOdds(item.odds)}</span>
                        <span className="col-implied">{item.impliedProb.toFixed(1)}%</span>
                        {ourProbability != null && item.edge != null && (
                          <span className={`col-edge ${item.edge > 0 ? 'positive' : item.edge < -2 ? 'negative' : 'neutral'}`}>
                            {item.edge > 0 ? '+' : ''}{item.edge.toFixed(1)}%
                          </span>
                        )}
                        <span className="col-bet">
                          {betLink ? (
                            <a
                              href={betLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bet-link-btn"
                              title={`Open ${item.name}`}
                            >
                              <IoOpenOutline size={14} />
                            </a>
                          ) : (
                            <span className="bet-link-na">-</span>
                          )}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Value explanation */}
              {ourProbability != null && (
                <div className="odds-comparison-legend">
                  <span className="legend-item positive">Positive edge = value bet</span>
                  <span className="legend-item">Edge = Our prob - Implied prob</span>
                </div>
              )}
            </>
          ) : (
            <div className="odds-comparison-empty">
              <p>No odds available for this selection</p>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  )
}
